import { useCallback, useEffect, useState } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import { LoadingModal } from '@adc/parallax-component-library';
import type { AxiosError } from 'axios';
import { MainContainer } from 'Components/utility';
import { APP_RETURN_TIMEOUT } from 'Constants';
import { jwtDecode } from 'jwt-decode';
import { AUTH_SESSION } from 'Reducers/auth/types';
import { getPatient } from 'Services/patient';
import type { RootState } from 'src/reducers';
import { notifyApp } from 'Utilities/appCommunicator';
import log from 'Utilities/log';
import mediator from 'Utilities/mediator';
import { persistAuthSession } from 'Utilities/session';
import { store } from 'Utilities/store';

const mapStateToProps = ({
  nav: {
    query: { token, osType, osVersion, app, appVersion },
  },
}: RootState) => ({
  token,
  osType,
  osVersion,
  app,
  appVersion,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const VerifyConsents = ({ token, osType, osVersion, app, appVersion }: Props) => {
  const [loading, setLoading] = useState(false);

  const handleError = useCallback((error?: AxiosError<ApiErrorData>) => {
    notifyApp('adc-webview:navigation', error);

    // Give the app time to redirect the user back
    setTimeout(() => {
      setLoading(false);
    }, APP_RETURN_TIMEOUT);
  }, []);

  const verifyConsents = useCallback(
    async (token: string) => {
      setLoading(true);

      const decodedToken = jwtDecode<{ id: string; did: string; exp: number; iat: number }>(token);

      try {
        const patient = await getPatient(decodedToken.id, osType, osVersion, app, appVersion);

        // Store the user session
        const authSession: AuthSession = {
          status: null,
          access_token: token,
          token_type: 'Bearer',
          account_id: decodedToken.id,
          expires: decodedToken.exp,
          include: { patient },
        };

        persistAuthSession(authSession);

        store.dispatch({ type: AUTH_SESSION, authSession });

        if (!patient.consents?.hipaa?.at) {
          mediator.publish('router:navigate', '/hipaa');
          return;
        } else if (!patient.consents?.realWorldEvidence?.at) {
          mediator.publish('router:navigate', '/rwe-consent');
          return;
        }

        handleError();
      } catch (error) {
        log.error('Failed to load the consents', error);
        handleError(error as AxiosError<ApiErrorData>);
      }
    },
    [app, appVersion, handleError, osType, osVersion]
  );

  useEffect(() => {
    if (token) {
      void verifyConsents(token);
    }
  }, [token, verifyConsents]);

  return <MainContainer>{loading && <LoadingModal aria-label="Loading" />}</MainContainer>;
};

export default connector(VerifyConsents);
